import React, { createContext, useContext, useState, useEffect } from 'react';
import lumoConversationService from '../services/lumoConversationService';
import lumoUpsellService from '../services/lumoUpsellService';
import gamificationService from '../services/gamificationService';
import dailyStatsService from '../services/dailyStatsService';
import { useAuth } from './AuthContext';

const LumoChatContext = createContext();

export const useLumoChat = () => {
  const context = useContext(LumoChatContext);
  if (!context) {
    // Return default values instead of throwing during initialization
    return {
      isOpen: false,
      messages: [],
      isTyping: false,
      unreadCount: 0,
      openChat: () => {},
      closeChat: () => {},
      toggleChat: () => {},
      sendMessage: () => {},
      clearChat: () => {},
      executeAction: () => {}
    };
  }
  return context;
};

export const LumoChatProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const { user, profile } = useAuth();

  // Load conversation history from database on mount
  useEffect(() => {
    const loadHistory = async () => {
      if (!user || messages.length > 0 || isLoadingHistory) return;
      
      setIsLoadingHistory(true);
      try {
        const history = await lumoConversationService.getConversationHistory(user.id);
        
        if (history && history.length > 0) {
          // Convert database format to UI format
          const formattedMessages = history.flatMap((msg, idx) => [
            {
              id: `user-${idx}`,
              type: 'user',
              message: msg.user_message,
              timestamp: new Date(msg.timestamp)
            },
            {
              id: `lumo-${idx}`,
              type: 'lumo',
              message: msg.lumo_response,
              actions: msg.actions || [],
              timestamp: new Date(msg.timestamp)
            }
          ]);
          
          setMessages(formattedMessages);
        } else {
          // No history - show welcome message
          const welcomeMessage = {
            id: 'welcome',
            type: 'lumo',
            message: "Hey! I'm Lumo, your AI sales coach. 👋\n\nI can help you practice, generate scripts, or answer questions. What would you like to work on?",
            actions: [
              { label: 'Start a Drill', type: 'navigate', path: '/daily-drill' },
              { label: 'Ask a Question', type: 'focus_input' }
            ],
            timestamp: new Date()
          };
          setMessages([welcomeMessage]);
        }
      } catch (error) {
        console.error('Error loading chat history:', error);
        // Show welcome message on error
        const welcomeMessage = {
          id: 'welcome',
          type: 'lumo',
          message: "Hey! I'm Lumo, your AI sales coach. 👋\n\nI can help you practice, generate scripts, or answer questions. What would you like to work on?",
          actions: [
            { label: 'Start a Drill', type: 'navigate', path: '/daily-drill' },
            { label: 'Ask a Question', type: 'focus_input' }
          ],
          timestamp: new Date()
        };
        setMessages([welcomeMessage]);
      } finally {
        setIsLoadingHistory(false);
      }
    };

    loadHistory();
  }, [user, messages.length, isLoadingHistory]);

  // Subscribe to milestone events for proactive upsells
  useEffect(() => {
    if (!user || !profile) return;

    const unsubscribe = dailyStatsService.subscribeMilestones(async (milestone) => {
      // Trigger upsell based on milestone type
      await triggerProactiveUpsell(milestone.type);
    });

    return () => unsubscribe();
  }, [user, profile]);

  // Send message to Lumo
  const sendMessage = async (messageText, currentPath = '/') => {
    if (!messageText?.trim()) return;

    // Add user message
    const userMessage = {
      id: Date.now().toString(),
      type: 'user',
      message: messageText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsTyping(true);

    try {
      // Get current page context
      const context = {
        page: currentPath,
        userLevel: profile?.level || 1,
        industry: profile?.industry || 'General'
      };

      // Send to Lumo service
      const response = await lumoConversationService.sendMessage(messageText, context);

      // Add Lumo response
      const lumoMessage = {
        id: (Date.now() + 1).toString(),
        type: 'lumo',
        message: response.message,
        actions: response.actions || [],
        data: response.data,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, lumoMessage]);

      // If chat is closed, increment unread count
      if (!isOpen) {
        setUnreadCount(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error sending message to Lumo:', error);
      
      // Add error message
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        type: 'lumo',
        message: "Oops! I had trouble processing that. Can you try again?",
        actions: [],
        timestamp: new Date()
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  // Open chat
  const openChat = () => {
    setIsOpen(true);
    setUnreadCount(0);
  };

  // Close chat
  const closeChat = () => {
    setIsOpen(false);
  };

  // Toggle chat
  const toggleChat = () => {
    if (isOpen) {
      closeChat();
    } else {
      openChat();
    }
  };

  // Clear chat history
  const clearChat = () => {
    const welcomeMessage = messages.find(m => m.id === 'welcome');
    setMessages(welcomeMessage ? [welcomeMessage] : []);
  };

  // Trigger proactive upsell based on milestone
  const triggerProactiveUpsell = async (context) => {
    if (!user || !profile) return;
    
    // Don't spam - check if upsell is appropriate
    if (!lumoUpsellService.canShowUpsell(context)) return;

    try {
      const streak = await gamificationService.getStreak();
      const stats = {
        streak: streak?.currentStreak || 0,
        level: profile?.level || 1
      };

      const upsell = lumoUpsellService.getStrategicUpsell(profile, context, stats);
      
      if (upsell) {
        const upsellMessage = lumoUpsellService.formatUpsellMessage(upsell);
        
        if (upsellMessage) {
          const lumoMessage = {
            id: Date.now().toString(),
            type: 'lumo',
            message: upsellMessage.message,
            actions: upsellMessage.actions || [],
            timestamp: new Date()
          };

          setMessages(prev => [...prev, lumoMessage]);
          setUnreadCount(prev => prev + 1);
          
          // Mark upsell shown
          lumoUpsellService.markUpsellShown(context);
          
          // Track event
          await lumoUpsellService.trackUpsellEvent('proactive_upsell_shown', {
            context,
            tier: upsell.tier,
            currentPlan: profile?.subscription_plan || 'free'
          });
        }
      }
    } catch (error) {
      console.error('Error triggering proactive upsell:', error);
    }
  };

  // Execute action from Lumo response
  const executeAction = (action) => {
    if (action.type === 'chat') {
      sendMessage(action.message);
    } else if (action.type === 'navigate') {
      window.location.href = action.path;
    } else if (action.type === 'focus_input') {
      // Focus will be handled by chat component
    }
  };

  const value = {
    isOpen,
    messages,
    isTyping,
    unreadCount,
    openChat,
    closeChat,
    toggleChat,
    sendMessage,
    clearChat,
    executeAction,
    triggerProactiveUpsell // Expose for milestone triggers
  };

  return (
    <LumoChatContext.Provider value={value}>
      {children}
    </LumoChatContext.Provider>
  );
};

export default LumoChatContext;
